// video segmentation helpers

import { useCallback, useMemo } from "react";

// ===== FRAME INDEXING CONSISTENCY METHODS =====
// WARNING: These methods must stay identical to their siblings in app/functions/helpers/video.ts
// Any changes here require corresponding changes in the backend file

function calcFrameTimestampFromIndex(frameIndex: number, fpsRate: number): number {
    return frameIndex / fpsRate;
}

function calcFrameIndexFromTimestamp(timestamp: number, fpsRate: number): number {
    return Math.round(timestamp * fpsRate);
}
// ===== END FRAME INDEXING CONSISTENCY METHODS =====

export const calcSecondsPerFrame = (fpsRate: number) => {
    return 1 / fpsRate;
};

export const calcFrameCount = (duration: number, fpsRate: number) => {
    if (duration === 0) {
        return 0;
    }

    return Math.max(1, Math.round(duration * fpsRate));
};

export const calcSegmentForTargetTime = (targetTime: number, secondsPerFrame: number) => {
    // Use floor instead of round so segments change at the exact second boundary
    // 0.0s to 0.999s -> segment 0
    // 1.0s to 1.999s -> segment 1
    // For 1 FPS: 0.5s -> floor(0.5 / 1) = 0, 1.0s -> floor(1.0 / 1) = 1
    // For 30 FPS: 0.01s -> floor(0.01 / 0.033) = 0, 0.05s -> floor(0.05 / 0.033) = 1
    return Math.floor(targetTime / secondsPerFrame);
};

export const calcClampedTimeForSegment = (segment: number, secondsPerFrame: number) => {
    return segment * secondsPerFrame;
};

export const calcClampedTimeForTargetTime = (targetTime: number, secondsPerFrame: number) => {
    const segment = calcSegmentForTargetTime(targetTime, secondsPerFrame);
    return calcClampedTimeForSegment(segment, secondsPerFrame);
};

export const useVideoSegmentation = (durationSeconds: number, fpsRate: number = 30) => {
    const secondsPerFrame = useMemo(() => calcSecondsPerFrame(fpsRate), [fpsRate]);

    const frameCount = useMemo(
        () => calcFrameCount(durationSeconds, fpsRate),
        [durationSeconds, fpsRate]
    );

    const segmentForTargetTime = useCallback(
        (targetTime: number) => {
            return calcSegmentForTargetTime(targetTime, secondsPerFrame);
        },
        [secondsPerFrame]
    );

    const clampedTimeForSegment = useCallback(
        (segment: number) => {
            return calcClampedTimeForSegment(segment, secondsPerFrame);
        },
        [secondsPerFrame]
    );

    const clampedTimeForTargetTime = useCallback(
        (targetTime: number) => {
            return calcClampedTimeForTargetTime(targetTime, secondsPerFrame);
        },
        [secondsPerFrame]
    );

    return {
        secondsPerFrame,
        segmentForTargetTime,
        clampedTimeForSegment,
        clampedTimeForTargetTime,
        frameCount
    };
};
